import { Form, Formik, type FormikErrors, type FormikHelpers } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { addTemplateStep, updateTemplateStep } from "@/features/notices/template-api";
import type { NoticeTemplateStep } from "@/features/notices/types";
import { applyApiErrors } from "@/lib/form-errors";

interface Values {
  description: string;
  link: string;
}

const validationSchema = Yup.object({
  description: Yup.string().trim().required("A step description is required"),
  link: Yup.string().url("Enter a valid URL").nullable(),
});

interface TemplateStepDialogProps {
  code: string;
  /** `null` = create a new step; otherwise edit this one. */
  step: NoticeTemplateStep | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

/** Create/edit a single firm notice-template step: description + optional link. */
export function TemplateStepDialog({
  code,
  step,
  open,
  onOpenChange,
  onSaved,
}: TemplateStepDialogProps) {
  const editing = step !== null;

  async function handleSubmit(values: Values, bag: FormikHelpers<Values>) {
    const payload = { description: values.description.trim(), link: values.link.trim() };
    try {
      if (editing) await updateTemplateStep(code, step.id, payload);
      else await addTemplateStep(code, payload);
      toast.success(editing ? "Step updated" : "Step added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const setErrors = bag.setErrors as (errors: FormikErrors<never>) => void;
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save step." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Template Step" : "Add Template Step"}</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={{ description: step?.description ?? "", link: step?.link ?? "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              <FormField
                name="description"
                label="Step"
                placeholder="e.g. Request wage & income transcripts"
              />
              <FormField name="link" label="Reference Link (optional)" placeholder="https://…" />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {editing ? "Save Changes" : "Add Step"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
